import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  GraduationCap,
  UserCog,
  DoorOpen,
  Wallet,
  CalendarCheck,
  TrendingUp,
  AlertCircle,
  Receipt,
  ArrowRight,
  Sparkles,
  Trophy,
  BookOpen,
  ClipboardList,
  Bus,
  MessageSquare,
  Activity,
  Users,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { UpcomingEventsWidget } from "@/pages/Calendar";

// ─── Chart helpers (colors validated for CVD-safety + light-surface contrast;
// see dataviz skill — contrast WARN slots always carry a direct/legend label) ──
const CHART_GRID = "#e1e0d9";
const CHART_AXIS = "#c3c2b7";
const CHART_MUTED = "#898781";

function fmtDay(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

function compactINR(v) {
  const n = Number(v) || 0;
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${n}`;
}

function ChartTooltip({ active, payload, label, prefix = "", suffix = "" }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 shadow-md text-xs">
      <div className="font-semibold text-slate-700 mb-0.5">{fmtDay(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="text-slate-600">
          {prefix}
          {typeof p.value === "number" ? p.value.toLocaleString("en-IN") : p.value}
          {suffix}
        </div>
      ))}
    </div>
  );
}

function ChartCard({ title, subtitle, icon: Icon, accent = "indigo", children }) {
  const accents = {
    indigo: "bg-indigo-50 text-indigo-700",
    emerald: "bg-emerald-50 text-emerald-700",
    violet: "bg-violet-50 text-violet-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div className="border border-slate-200 bg-white rounded-xl overflow-hidden shadow-sm">
      <div className="px-3.5 py-2.5 border-b border-slate-200 flex items-center gap-2">
        <div
          className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${accents[accent]}`}
        >
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-sm text-slate-800 leading-tight truncate">
            {title}
          </div>
          {subtitle && (
            <div className="text-[11px] text-slate-400 leading-tight">
              {subtitle}
            </div>
          )}
        </div>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

/** Part-to-whole: a single horizontal stacked bar + a legend/table row per segment
 * (segments carry the direct labels, since two of the validated categorical
 * slots sit under 3:1 contrast on a light surface). */
function StackedShareBar({ segments }) {
  const total = segments.reduce((sum, seg) => sum + (seg.value || 0), 0);
  if (total <= 0) {
    return (
      <div className="text-xs text-slate-400 text-center py-9">
        No data yet
      </div>
    );
  }
  const row = segments.reduce(
    (acc, seg) => ({ ...acc, [seg.key]: seg.value }),
    { name: "share" },
  );

  return (
    <div>
      <ResponsiveContainer width="100%" height={40}>
        <BarChart
          data={[row]}
          layout="vertical"
          margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
        >
          <XAxis type="number" hide domain={[0, total]} />
          <YAxis type="category" dataKey="name" hide />
          <Tooltip
            cursor={{ fill: "transparent" }}
            formatter={(value, key) => {
              const seg = segments.find((s) => s.key === key);
              const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
              return [`${value.toLocaleString("en-IN")} (${pct}%)`, seg?.label || key];
            }}
          />
          {segments.map((seg, i) => (
            <Bar
              key={seg.key}
              dataKey={seg.key}
              stackId="share"
              fill={seg.color}
              stroke="#fff"
              strokeWidth={2}
              radius={
                i === 0
                  ? [6, 0, 0, 6]
                  : i === segments.length - 1
                    ? [0, 6, 6, 0]
                    : [0, 0, 0, 0]
              }
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2.5">
        {segments.map((seg) => (
          <div key={seg.key} className="flex items-center gap-1.5 text-[11px]">
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: seg.color }}
            />
            <span className="text-slate-600 font-semibold">{seg.label}</span>
            <span className="text-slate-400">
              {seg.value.toLocaleString("en-IN")} ·{" "}
              {total > 0 ? ((seg.value / total) * 100).toFixed(0) : 0}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  accent = "indigo",
  trend,
  onClick,
}) {
  const accents = {
    indigo: {
      bg: "from-indigo-500 to-indigo-700",
      icon: "bg-indigo-100 text-indigo-700",
    },
    emerald: {
      bg: "from-emerald-500 to-emerald-700",
      icon: "bg-emerald-100 text-emerald-700",
    },
    amber: {
      bg: "from-amber-400 to-amber-600",
      icon: "bg-amber-100 text-amber-700",
    },
    rose: {
      bg: "from-rose-500 to-rose-700",
      icon: "bg-rose-100 text-rose-700",
    },
    sky: { bg: "from-sky-500 to-sky-700", icon: "bg-sky-100 text-sky-700" },
    violet: {
      bg: "from-violet-500 to-violet-700",
      icon: "bg-violet-100 text-violet-700",
    },
  };
  const a = accents[accent];
  return (
    <button
      onClick={onClick}
      data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}
      className="group relative overflow-hidden text-left bg-white border border-slate-200 rounded-xl p-3.5 hover:shadow-lg hover:-translate-y-0.5 hover:border-[var(--erp-accent)] transition-all duration-200"
    >
      <div
        className={`absolute -right-6 -top-6 w-24 h-24 rounded-full bg-gradient-to-br ${a.bg} opacity-10 group-hover:opacity-20 transition-opacity`}
      />
      <div className="flex items-start justify-between mb-2 relative">
        <div
          className={`w-10 h-10 rounded-lg ${a.icon} flex items-center justify-center shadow-sm`}
        >
          <Icon className="w-5 h-5" />
        </div>
        {trend != null && (
          <span
            className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${trend >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}
          >
            {trend >= 0 ? "↑" : "↓"} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </div>
      <div className="text-2xl font-bold tracking-tight text-slate-900 mt-1 font-serif-display">
        {value}
      </div>
      {sub && (
        <div className="text-[11px] text-slate-500 mt-0.5 truncate">{sub}</div>
      )}
    </button>
  );
}

function QuickAction({ icon: Icon, label, to, color = "indigo" }) {
  const nav = useNavigate();
  const colors = {
    indigo: "from-indigo-600 to-indigo-800",
    emerald: "from-emerald-600 to-emerald-800",
    rose: "from-rose-600 to-rose-800",
    amber: "from-amber-500 to-amber-700",
    sky: "from-sky-600 to-sky-800",
    violet: "from-violet-600 to-violet-800",
  };
  return (
    <button
      onClick={() => nav(to)}
      data-testid={`qa-${label.replace(/\s+/g, "-").toLowerCase()}`}
      className={`group relative overflow-hidden rounded-xl bg-gradient-to-br ${colors[color]} text-white p-3.5 text-left hover:shadow-xl transition-all duration-200 hover:scale-[1.02]`}
    >
      <div className="absolute -right-3 -bottom-3 opacity-15 group-hover:opacity-25 transition-opacity">
        <Icon className="w-16 h-16" />
      </div>
      <Icon className="w-5 h-5 mb-2" />
      <div className="text-sm font-semibold">{label}</div>
      <ArrowRight className="w-3.5 h-3.5 mt-1 opacity-70 group-hover:translate-x-1 transition-transform" />
    </button>
  );
}

export default function Dashboard() {
  const { branchId, sessionId, user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [feeSummary, setFeeSummary] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const params = {};
        if (branchId) params.branch_id = branchId;
        if (sessionId) params.session_id = sessionId;
        const { data } = await api.get("/dashboard/stats", { params });
        setStats(data);
      } catch (e) {
        console.error(e);
      }
    })();
    (async () => {
      try {
        const params = {};
        if (branchId) params.branch_id = branchId;
        if (sessionId) params.session_id = sessionId;
        const { data } = await api.get("/fees/collection-summary", { params });
        setFeeSummary(data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [branchId, sessionId]);

  const s = stats || {};
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  const attendanceTrend = s.attendance_trend || [];
  const feeTrend = s.fee_trend || [];
  const classWiseStrength = s.class_wise_strength || [];

  const genderSegments = useMemo(() => {
    const g = s.gender_ratio || { male: 0, female: 0, other: 0 };
    const segs = [
      { key: "male", label: "Male", value: g.male || 0, color: "#6366f1" },
      { key: "female", label: "Female", value: g.female || 0, color: "#f43f5e" },
    ];
    if (g.other > 0) {
      segs.push({ key: "other", label: "Other", value: g.other, color: "#f59e0b" });
    }
    return segs;
  }, [s.gender_ratio]);

  const feeModeSegments = useMemo(() => {
    const byMode = feeSummary?.by_mode || {};
    const entries = Object.entries(byMode)
      .map(([label, value]) => ({ label, value: Number(value) || 0 }))
      .filter((e) => e.value > 0)
      .sort((a, b) => b.value - a.value);
    const palette = ["#6366f1", "#10b981", "#f59e0b", "#f43f5e", "#0ea5e9"];
    const top = entries.slice(0, 5).map((e, i) => ({
      key: `mode_${i}`,
      label: e.label,
      value: e.value,
      color: palette[i],
    }));
    const restTotal = entries.slice(5).reduce((sum, e) => sum + e.value, 0);
    if (restTotal > 0) {
      top.push({ key: "mode_other", label: "Other", value: restTotal, color: "#94a3b8" });
    }
    return top;
  }, [feeSummary]);

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      {/* Hero greeting */}
      <div className="relative overflow-hidden rounded-2xl erp-gradient text-white px-5 py-6 shadow-lg">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />
        <div className="absolute -right-10 -bottom-10 opacity-10">
          <GraduationCap className="w-48 h-48" />
        </div>
        <div className="relative">
          <div className="text-[11px] uppercase tracking-widest opacity-80 mb-1">
            {new Date().toLocaleDateString("en-IN", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight font-serif-display">
            {greeting}, {user?.name?.split(" ")[0] || "Admin"}{" "}
            <span className="inline-block">👋</span>
          </h1>
          <p className="text-white/80 text-sm mt-1 max-w-2xl">
            Here's the pulse of your school today — collections, attendance,
            admissions and visitors at a glance.
          </p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        <StatCard
          icon={GraduationCap}
          label="Students"
          value={s.students_count ?? "—"}
          accent="indigo"
          onClick={() => nav("/students")}
        />
        <StatCard
          icon={UserCog}
          label="Staff"
          value={s.staff_count ?? "—"}
          accent="violet"
          onClick={() => nav("/staff")}
        />
        <StatCard
          icon={DoorOpen}
          label="Visitors Today"
          value={s.visitors_today ?? "—"}
          accent="amber"
          onClick={() => nav("/visitor")}
        />
        <StatCard
          icon={CalendarCheck}
          label="Attendance"
          value={`${(s.attendance_percent ?? 0).toFixed(1)}%`}
          accent="emerald"
          onClick={() => nav("/attendance")}
        />
        <StatCard
          icon={Wallet}
          label="Collected Today"
          value={`₹${(s.collected_today ?? 0).toLocaleString()}`}
          accent="emerald"
          onClick={() => nav("/fees/receipts")}
        />
        <StatCard
          icon={AlertCircle}
          label="Total Due"
          value={`₹${(s.total_due ?? 0).toLocaleString()}`}
          accent="rose"
          onClick={() => nav("/fees/outstanding")}
        />
        <StatCard
          icon={TrendingUp}
          label="Pending Leaves"
          value={s.pending_leaves ?? "—"}
          accent="amber"
        />
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-2.5 flex items-center gap-2">
          <Sparkles className="w-4 h-4 erp-text-primary" /> Quick Actions
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <QuickAction
            icon={Receipt}
            label="Collect Fee"
            to="/fees/receipt"
            color="emerald"
          />
          <QuickAction
            icon={GraduationCap}
            label="New Admission"
            to="/students/new"
            color="indigo"
          />
          <QuickAction
            icon={CalendarCheck}
            label="Mark Attendance"
            to="/attendance"
            color="sky"
          />
          <QuickAction
            icon={DoorOpen}
            label="Visitor Pass"
            to="/visitor"
            color="amber"
          />
          <QuickAction
            icon={Trophy}
            label="Bulk Allotment"
            to="/allotment"
            color="violet"
          />
          <QuickAction
            icon={MessageSquare}
            label="Send Circular"
            to="/communicate/circulars"
            color="rose"
          />
        </div>
      </div>

      {/* Analytics */}
      <div>
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-2.5 flex items-center gap-2">
          <Activity className="w-4 h-4 erp-text-primary" /> Analytics
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <ChartCard
            title="Attendance Trend"
            subtitle="Last 7 days"
            icon={CalendarCheck}
            accent="emerald"
          >
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                data={attendanceTrend}
                margin={{ top: 8, right: 12, left: -18, bottom: 0 }}
              >
                <CartesianGrid stroke={CHART_GRID} vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={fmtDay}
                  tick={{ fontSize: 11, fill: CHART_MUTED }}
                  axisLine={{ stroke: CHART_AXIS }}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fontSize: 11, fill: CHART_MUTED }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                />
                <Tooltip content={<ChartTooltip suffix="%" />} />
                <Line
                  type="monotone"
                  dataKey="value"
                  name="Attendance"
                  stroke="#059669"
                  strokeWidth={2}
                  dot={{ r: 4, fill: "#059669", stroke: "#fff", strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Fee Collection"
            subtitle="Last 7 days"
            icon={Wallet}
            accent="indigo"
          >
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={feeTrend}
                margin={{ top: 8, right: 12, left: -6, bottom: 0 }}
              >
                <CartesianGrid stroke={CHART_GRID} vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={fmtDay}
                  tick={{ fontSize: 11, fill: CHART_MUTED }}
                  axisLine={{ stroke: CHART_AXIS }}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) => `₹${compactINR(v)}`}
                  tick={{ fontSize: 11, fill: CHART_MUTED }}
                  axisLine={false}
                  tickLine={false}
                  width={48}
                />
                <Tooltip content={<ChartTooltip prefix="₹" />} />
                <Bar
                  dataKey="value"
                  name="Collected"
                  fill="#6366f1"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={28}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Class-wise Strength"
            subtitle="Active students per class"
            icon={GraduationCap}
            accent="violet"
          >
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={classWiseStrength}
                margin={{ top: 8, right: 12, left: -18, bottom: 0 }}
              >
                <CartesianGrid stroke={CHART_GRID} vertical={false} />
                <XAxis
                  dataKey="class_name"
                  tick={{ fontSize: 10, fill: CHART_MUTED }}
                  axisLine={{ stroke: CHART_AXIS }}
                  tickLine={false}
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  height={45}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: CHART_MUTED }}
                  axisLine={false}
                  tickLine={false}
                  width={30}
                  allowDecimals={false}
                />
                <Tooltip content={<ChartTooltip />} />
                <Bar
                  dataKey="count"
                  name="Students"
                  fill="#8b5cf6"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={28}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <div className="grid grid-cols-1 gap-3">
            <ChartCard
              title="Gender Ratio"
              subtitle="Active students"
              icon={Users}
              accent="rose"
            >
              <StackedShareBar segments={genderSegments} />
            </ChartCard>
            <ChartCard
              title="Collections by Mode"
              subtitle="All-time, top 5"
              icon={Receipt}
              accent="amber"
            >
              <StackedShareBar segments={feeModeSegments} />
            </ChartCard>
          </div>
        </div>
      </div>

      {/* Activity panels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Panel
          title="Recent Admissions"
          icon={GraduationCap}
          accent="indigo"
          link="/students"
        >
          {(s.recent_admissions || []).length === 0 && (
            <Empty label="No recent admissions" />
          )}
          {(s.recent_admissions || []).map((st) => (
            <Row
              key={st.id}
              left={`${st.first_name || ""} ${st.last_name || ""}`}
              right={st.admission_no}
            />
          ))}
        </Panel>
        <Panel
          title="Recent Receipts"
          icon={Receipt}
          accent="emerald"
          link="/fees/receipts"
        >
          {(s.recent_receipts || []).length === 0 && (
            <Empty label="No receipts yet" />
          )}
          {(s.recent_receipts || []).map((r) => (
            <Row
              key={r.id}
              left={r.receipt_no}
              right={`₹${(r.amount ?? r.total ?? 0).toLocaleString?.() ?? r.amount ?? r.total ?? 0}`}
              bold
            />
          ))}
        </Panel>
        <UpcomingEventsWidget />
      </div>
    </div>
  );
}

function Panel({ title, icon: Icon, accent, link, children }) {
  const accents = {
    indigo: "bg-indigo-50 text-indigo-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
  };
  const nav = useNavigate();
  return (
    <div className="border border-slate-200 bg-white rounded-xl overflow-hidden shadow-sm">
      <div className="px-3.5 py-2.5 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className={`w-7 h-7 rounded-md flex items-center justify-center ${accents[accent]}`}
          >
            <Icon className="w-3.5 h-3.5" />
          </div>
          <span className="font-semibold text-sm text-slate-800">{title}</span>
        </div>
        {link && (
          <button
            onClick={() => nav(link)}
            className="text-[11px] erp-text-primary font-semibold hover:underline flex items-center gap-0.5"
            data-testid={`panel-link-${title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            View all <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
      <div className="divide-y divide-slate-100 text-sm">{children}</div>
    </div>
  );
}
function Row({ left, right, bold }) {
  return (
    <div className="flex items-center justify-between px-3.5 py-2 hover:bg-slate-50">
      <span className="truncate font-medium text-slate-800">{left}</span>
      <span
        className={`text-xs ${bold ? "font-bold erp-text-primary" : "text-slate-500"}`}
      >
        {right}
      </span>
    </div>
  );
}
function Empty({ label }) {
  return (
    <div className="px-3 py-8 text-center text-xs text-slate-400">{label}</div>
  );
}
